import React, { useEffect, useMemo, useState } from 'react';
import { Search, ChevronDown, Wrench, Settings } from 'lucide-react';
import { mockToolsByAgent } from '../data/mockData';
import { Tool, ToolCategory } from '../types';
import { useAgent } from '../contexts/AgentContext';
import { AppCard } from '../components/base/AppCard';
import { AppBadge } from '../components/base/AppBadge';
import { AppSwitch } from '../components/base/AppSwitch';
export function StrumentiPage() {
  const {
    activeAgentId
  } = useAgent();
  const [tools, setTools] = useState<(Tool & {
    enabled: boolean;
  })[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('Tutte');
  useEffect(() => {
    const agentTools = mockToolsByAgent[activeAgentId] || mockToolsByAgent['default'];
    setTools(agentTools.tools);
  }, [activeAgentId]);
  const handleToggle = (toolId: string) => {
    setTools(prevTools => prevTools.map(tool => tool.id === toolId ? {
      ...tool,
      enabled: !tool.enabled
    } : tool));
  };
  const filteredTools = useMemo(() => {
    return tools.filter(tool => {
      const matchesSearch = searchQuery === '' || tool.name.toLowerCase().includes(searchQuery.toLowerCase()) || tool.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'Tutte' || tool.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [tools, searchQuery, categoryFilter]);
  const uniqueCategories: ToolCategory[] = ['Ricerca', 'Profilo utente', 'Analisi dati', 'Comunicazione', 'Integrazione'];
  return <div className="pb-12 space-y-8" data-id="element-2048">
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4" data-id="element-2049">
        <div className="flex-1 relative" data-id="element-2050">
          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-3" data-id="element-2051" />
          <input type="text" placeholder="Cerca strumenti..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all duration-180 shadow-sm" data-id="element-2052" />
        </div>

        <div className="relative min-w-[200px]" data-id="element-2053">
          <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="appearance-none w-full bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-subtle text-gray-700 dark:text-gray-300 py-2.5 pl-3.5 pr-10 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 cursor-pointer transition-all duration-180 shadow-sm" data-id="element-2054">
            <option value="Tutte" data-id="element-2055">Tutte le categorie</option>
            {uniqueCategories.map(cat => <option key={cat} value={cat} data-id="element-2056">
                {cat}
              </option>)}
          </select>
          <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-3 pointer-events-none" data-id="element-2057" />
        </div>
      </div>

      {/* Tools Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2058">
        {filteredTools.map(tool => <AppCard key={tool.id} className={`transition-all duration-200 ${!tool.enabled ? 'opacity-80' : ''}`} data-id="element-2059">
            <div className="flex items-start justify-between mb-4" data-id="element-2060">
              <div className="flex items-center gap-3" data-id="element-2061">
                <div className={`p-2.5 rounded-xl ${tool.enabled ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-dark-subtle text-gray-500 dark:text-gray-400'}`} data-id="element-2062">
                  <Wrench className="w-5 h-5" data-id="element-2063" />
                </div>
                <div data-id="element-2064">
                  <h3 className="text-[17px] font-semibold text-gray-900 dark:text-gray-100" data-id="element-2065">
                    {tool.name}
                  </h3>
                  <AppBadge variant="gray" className="mt-1" data-id="element-2066">
                    {tool.category}
                  </AppBadge>
                </div>
              </div>
              <AppSwitch checked={tool.enabled} onChange={() => handleToggle(tool.id)} data-id="element-2067" />
            </div>

            <p className="text-[15px] text-gray-600 dark:text-gray-400 leading-relaxed mb-6 min-h-[48px]" data-id="element-2068">
              {tool.description}
            </p>

            <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-dark-subtle" data-id="element-2069">
              <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-2070">
                <span className="font-medium text-gray-900 dark:text-gray-200" data-id="element-2071">
                  {tool.weeklyCallCount}
                </span>{' '}
                chiamate/sett
              </div>

              <button className="flex items-center text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors" data-id="element-2072">
                <Settings className="w-4 h-4 mr-1.5" data-id="element-2073" />
                Configurazione
              </button>
            </div>
          </AppCard>)}
      </div>
    </div>;
}